import { readAccessTokenClaims, tokenIdentity } from './access-token';

// The Doc Pabean draft editor keeps per-tab caches in sessionStorage: the
// master-ceisa profile it fills PPJK from (`userData`) and copies of the last
// draft it loaded. None of them name the account they were read for, so after
// a login, logout or impersonation switch they fed one account's data into
// another account's form. They belong to one identity and are dropped when it
// changes. Leaf module: no app imports.

const DRAFT_CACHE_KEYS = [
  'userData',
  'documentData',
  'goodsData',
  'nonGoodsData',
  'batchPdfFiles',
  'tpsDisplayData',
  'barang_visited',
  // Unscoped fallback key of the invoice cost components (komponenBiayaStorageKey without a process).
  'komponenBiayaData',
];
const USER_CONTROLLED_SUFFIX = '_userControlled';
// Caches keyed per process, AJU or batch (komponenBiayaStorageKey,
// pungutan*StorageKey in PungutanForm, the upload folder review meta).
const DRAFT_CACHE_PREFIXES = ['komponenBiayaData_', 'pungutanData_', 'pungutanReadiness_', 'jarvis.folderReview.'];
const IDENTITY_KEY = 'jarvis:cacheIdentity';

const session = (): Storage | undefined => {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  } catch {
    return undefined; // blocked storage: nothing cached, nothing to clear
  }
};

/**
 * Drops every draft cache in this tab. The identity stamp is left alone:
 * callers that change the session use syncTabCachesWithToken, which clears
 * and re-stamps in one step.
 */
export const clearTabSessionCaches = () => {
  const store = session();
  if (!store) return;
  try {
    for (const key of DRAFT_CACHE_KEYS) store.removeItem(key);
    const suffixed: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key && (key.endsWith(USER_CONTROLLED_SUFFIX) || DRAFT_CACHE_PREFIXES.some((p) => key.startsWith(p)))) {
        suffixed.push(key);
      }
    }
    for (const key of suffixed) store.removeItem(key);
  } catch {
    // ignore: a cache that cannot be read cannot leak either
  }
};

/**
 * Clears the caches when they were written for another identity than the
 * current access token's, then stamps the current one. Runs on every page
 * load and right after every token change (login, logout, impersonation start,
 * switch and stop), so a tab never pairs one account's caches with another's
 * token, and the caches of the account that stays are kept across reloads.
 */
export const syncTabCachesWithToken = () => {
  const store = session();
  if (!store) return;
  const current = tokenIdentity(readAccessTokenClaims());
  try {
    if (store.getItem(IDENTITY_KEY) !== current) {
      clearTabSessionCaches();
      store.setItem(IDENTITY_KEY, current);
    }
  } catch {
    // ignore
  }
};
